import React, { useState } from 'react';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// Partner sign-in. Two ways in, and which one is primary is the point:
//
//  - EMAIL CODE (default). A referral partner has signed an agreement, not
//    chosen a password — there is nothing for them to remember. The code
//    proves the mailbox, and /api/partner-auth creates the account behind it
//    if one does not exist yet.
//  - PASSWORD. The same POST /api/login (and 2FA follow-up) the owner login
//    uses, for a partner who is also an InvestMenter account holder.
//
// Both write the same localStorage keys the rest of the app reads, so signing
// in here signs you in everywhere, and someone already signed in as an owner
// never sees this screen.
//
// Signing in does NOT grant access to the referral tools: the server checks
// the agreement is signed on every referral call (referralRoutes'
// requirePartner). This screen only authenticates.
// ============================================================

const API_URL =
  (process.env.REACT_APP_API_URL ||
    'https://investmenter-backend-1081814658115.europe-west1.run.app') + '/api';

const PartnerLogin: React.FC<{
  onSignedIn: (investor: any) => void;
  onBack: () => void;
  onChangeRole?: () => void;
}> = ({ onSignedIn, onBack, onChangeRole }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState<'code' | 'password'>('code');
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  // Every successful path hands back the same payload, so one place stores it.
  const accept = (data: any) => {
    const next = {
      ...data.investor,
      vipStatus: data.crmData?.vipStatus || data.investor.vipStatus || null,
    };
    try {
      if (data.token) localStorage.setItem('authToken', data.token);
      localStorage.setItem('investorId', next.id);
      localStorage.setItem('investorEmail', next.email);
      localStorage.setItem('investor', JSON.stringify(next));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch {
      /* blocked storage shouldn't stop the sign-in */
    }
    onSignedIn(next);
  };

  const backToEnter = () => { setStep('enter'); setCode(''); setErr(''); setInfo(''); };

  const switchMode = (to: 'code' | 'password') => {
    setMode(to); backToEnter(); setPassword('');
  };

  const sendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setErr(t('pnEnterEmail')); return; }
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/partner-auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e }),
      });
      const data = await res.json();
      if (data.success) { setInfo(`${t('pnCodeSentTo')} ${e}`); setStep('confirm'); }
      else setErr(data.error || t('pnSignInFailed'));
    } catch { setErr(t('pnSignInFailed')); } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    if (code.length !== 6) { setErr(t('pnEnterCode')); return; }
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/partner-auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code }),
      });
      const data = await res.json();
      if (data.success && data.requires2FA) {
        // Authenticator on: continue on the password flow's 2FA step
        setMode('password'); setTempToken(data.tempToken); setCode(''); setInfo(''); setStep('confirm');
      }
      else if (data.success && data.investor) accept(data);
      else setErr(data.error || t('pnCodeInvalid'));
    } catch { setErr(t('pnSignInFailed')); } finally { setBusy(false); }
  };

  const signInWithPassword = async () => {
    if (!email.trim() || !password) { setErr(t('pnEnterCredentials')); return; }
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (data.success && data.requires2FA) { setTempToken(data.tempToken); setCode(''); setStep('confirm'); }
      else if (data.success && data.investor) accept(data);
      else setErr(data.error || t('pnSignInFailed'));
    } catch { setErr(t('pnSignInFailed')); } finally { setBusy(false); }
  };

  const verify2FA = async () => {
    if (code.length !== 6) { setErr(t('pnEnterCode')); return; }
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code }),
      });
      const data = await res.json();
      if (data.success && data.investor) accept(data);
      else setErr(data.error || t('pnCodeInvalid'));
    } catch { setErr(t('pnSignInFailed')); } finally { setBusy(false); }
  };

  const submit = mode === 'code'
    ? (step === 'enter' ? sendCode : verifyCode)
    : (step === 'enter' ? signInWithPassword : verify2FA);

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') submit(); };

  const title = step === 'confirm'
    ? t(mode === 'code' ? 'pnCheckInbox' : 'pnCheckAuthenticator')
    : t('pnAccessTitle');
  const sub = step === 'confirm'
    ? t(mode === 'code' ? 'pnCodeSub' : 'pn2faSub')
    : t(mode === 'code' ? 'pnLoginSubCode' : 'pnLoginSubPassword');

  return (
    <div className="pt-screen pl-screen">
      <div className="pl-frame" aria-hidden="true" />

      <div className="pl-content">
        <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />
        <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>

        <div className="pl-eyebrow">{t('pnAccessEyebrow')}</div>
        <h1 className="pl-title">{title}</h1>
        <p className="pl-sub">{sub}</p>

        <div className="pl-sheet">
          {step === 'enter' ? (
            <>
              <label className="pl-label" htmlFor="partner-email">{t('email')}</label>
              <input
                id="partner-email"
                className="pl-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={onKey}
                autoComplete="username"
                placeholder="you@example.com"
              />

              {mode === 'password' && (
                <>
                  <label className="pl-label" htmlFor="partner-password">{t('pnPassword')}</label>
                  <input
                    id="partner-password"
                    className="pl-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={onKey}
                    autoComplete="current-password"
                    placeholder="••••••••"
                  />
                </>
              )}

              <button onClick={submit} disabled={busy} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t(mode === 'code' ? 'pnSendCode' : 'pnSignIn')}
              </button>

              <div className="pl-links">
                <button className="pl-link" onClick={() => switchMode(mode === 'code' ? 'password' : 'code')}>
                  {t(mode === 'code' ? 'pnUsePassword' : 'pnUseCode')}
                </button>
              </div>

              <p className="pl-note">{t(mode === 'code' ? 'rfSignInNote' : 'pnSameAccountNote')}</p>
            </>
          ) : (
            <>
              <label className="pl-label" htmlFor="partner-code">{t('pnCodeLabel')}</label>
              <input
                id="partner-code"
                className="pl-input pl-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={onKey}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
              />
              <button onClick={submit} disabled={busy} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t('pnVerifyContinue')}
              </button>
              <div className="pl-links">
                <button className="pl-link" onClick={backToEnter}>{t('pnBackToSignIn')}</button>
                {mode === 'code' && (
                  <button className="pl-link" onClick={sendCode} disabled={busy}>{t('pnResendCode')}</button>
                )}
              </div>
            </>
          )}

          {info && !err && <p className="pl-note">{info}</p>}
          {err && <p className="pl-err">{err}</p>}
        </div>

        <button onClick={onBack} className="pl-changerole">← {t('pnBackToProgramme')}</button>
        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

export default PartnerLogin;
